<template>
  <div
    v-if="amount > 0"
    class="v360-main h-full flex flex-col min-h-0"
  >
    <div class="v360-viewer-container relative flex-1 min-h-0">
      <div
        ref="containerEl"
        class="v360-viewport h-full overflow-hidden"
        tabindex="0"
        :style="containerStyle"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @keydown="onKeydown"
      >
        <div
          class="v360-image-container h-full w-full"
          :style="imageContainerStyle"
        >
          <img
            v-if="currentSrc"
            :src="currentSrc"
            class="object-contain"
            draggable="false"
            alt="360° product view"
            @dragstart.prevent
          >
        </div>
      </div>
    </div>

    <div class="v360-navigate-btns flex gap-4 justify-center">
      <button
        type="button"
        class="v360-menu-btns"
        :class="[menuBtnBaseClasses, menuBtnFilledClasses]"
        :aria-label="isPlaying ? 'Pause' : 'Play'"
        @click="togglePlay"
      >
        <span
          v-if="!isPlaying"
          class="iconify fa-pro-solid--play text-sm"
        />
        <span
          v-else
          class="iconify svg--pause2 text-xs"
        />
      </button>

      <template v-if="!disableZoom">
        <button
          type="button"
          aria-label="Zoom in"
          class="v360-menu-btns"
          :class="[menuBtnBaseClasses, menuBtnFilledClasses]"
          @click="zoomIn"
        >
          <span class="iconify svg--accordion-open text-xs" />
        </button>

        <button
          type="button"
          aria-label="Zoom out"
          class="v360-menu-btns"
          :class="[menuBtnBaseClasses, menuBtnFilledClasses]"
          @click="zoomOut"
        >
          <span class="iconify svg--accordion-close text-xs" />
        </button>
      </template>

      <button
        type="button"
        aria-label="Reset view"
        class="v360-menu-btns"
        :class="[menuBtnBaseClasses, menuBtnFilledClasses]"
        @click="resetPosition"
      >
        <span class="iconify fa-pro-solid--rotate-left" />
      </button>

      <button
        type="button"
        aria-label="Previous frame"
        class="v360-menu-btns v360-menu-btns--empty"
        :class="[
          menuBtnBaseClasses,
          sideArrows
            ? 'v360-menu-btns--left-side w-9 h-9 border-2 rounded absolute left-2 top-1/2 -translate-y-1/2'
            : '',
        ]"
        @click="prev"
      >
        <span class="iconify svg--chevron-left text-xl" />
      </button>

      <button
        type="button"
        aria-label="Next frame"
        class="v360-menu-btns v360-menu-btns--empty"
        :class="[
          menuBtnBaseClasses,
          sideArrows
            ? 'v360-menu-btns--right-side w-9 h-9 border-2 rounded absolute right-2 top-1/2 -translate-y-1/2'
            : '',
        ]"
        @click="next"
      >
        <span class="iconify svg--chevron-right text-xl" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { CSSProperties } from 'vue';

const DEFAULT_PIXELS_PER_FRAME = 12;
type LoopProp = number | boolean;

type Props = {
  amount: number;
  imagePath: string;
  fileName: string;
  autoplay?: boolean;
  autoplayIntervalMs?: number;
  pauseOnHover?: boolean;
  spinReverse?: boolean;
  disableScrollEvent?: boolean;
  loop?: LoopProp;
  pixelsPerFrame?: number;
  disableZoom?: boolean;
  sideArrows?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  autoplay: false,
  autoplayIntervalMs: 100,
  pauseOnHover: false,
  spinReverse: false,
  disableScrollEvent: false,
  loop: false,
  pixelsPerFrame: DEFAULT_PIXELS_PER_FRAME,
  disableZoom: false,
  sideArrows: false,
});

const currentFrame = ref(1);
const isInteracting = ref(false);
const isHovering = ref(false);
const isInView = ref(true);
const isPlaying = ref(false);

const lastX = ref(0);
const fractionalFrameRemainder = ref(0);
const activePointerId = ref<number | null>(null);
const containerEl = ref<HTMLElement | null>(null);

const zoom = ref(1);
const panMode = ref(false);

const rotationsCompleted = ref(0);
let autoplayTimerId: number | null = null;
let intersectionObserver: IntersectionObserver | null = null;

const menuBtnBaseClasses =
  'h-6 w-6 flex items-center justify-center cursor-pointer text-EXPONDO-primary-80';
const menuBtnFilledClasses = 'rounded-full bg-EXPONDO-secondary-light';

const joinUrl = (base: string, path: string): string => {

  const trimmedBase = (base ?? '').trim();
  const trimmedPath = (path ?? '').trim();

  if (!trimmedBase) {

    return trimmedPath;

  }

  if (!trimmedPath) {

    return trimmedBase;

  }

  const baseEndsWithSlash = trimmedBase.endsWith('/');
  const pathStartsWithSlash = trimmedPath.startsWith('/');

  if (baseEndsWithSlash && pathStartsWithSlash) {

    return `${trimmedBase}${trimmedPath.slice(1)}`;

  }

  if (!baseEndsWithSlash && !pathStartsWithSlash) {

    return `${trimmedBase}/${trimmedPath}`;

  }

  return `${trimmedBase}${trimmedPath}`;

};

const buildFrameFileName = (index: number): string => {

  const template = props.fileName ?? '';

  if (!template) {

    return '';

  }

  if (template.includes('{index}')) {

    return template.replaceAll('{index}', String(index));

  }

  return `${template}_${index}`;

};

const frameUrls = computed<string[]>(() => {

  const total = Number(props.amount);

  if (!Number.isFinite(total) || total <= 0) {

    return [];

  }

  const urls: string[] = [];
  for (let index = 1; index <= total; index += 1) {

    const frameFile = buildFrameFileName(index);

    if (!frameFile) {

      continue;

    }
    urls.push(joinUrl(props.imagePath, frameFile));

  }

  return urls;

});

const currentSrc = computed(() => {

  const urls = frameUrls.value;

  if (!urls.length) {

    return '';

  }

  const frame = Math.min(Math.max(currentFrame.value, 1), urls.length);

  return urls[frame - 1] ?? '';

});

const clampFrame = (frame: number): number => {

  const total = frameUrls.value.length;

  if (total <= 0) {

    return 1;

  }

  return Math.min(Math.max(frame, 1), total);

};

const pixelsPerFrameResolved = computed(() => {

  const ppf = Number(props.pixelsPerFrame);

  return Number.isFinite(ppf) && ppf > 0 ? ppf : DEFAULT_PIXELS_PER_FRAME;

});

const directionStep = computed(() => (props.spinReverse ? -1 : 1));

const wrapFrame = (frame: number): number => {

  const total = frameUrls.value.length;

  if (total <= 0) {

    return 1;

  }

  const normalized = ((frame - 1) % total + total) % total;

  return normalized + 1;

};

const stepByFrames = (deltaFrames: number) => {

  if (!deltaFrames) {

    return;

  }

  currentFrame.value = wrapFrame(currentFrame.value + deltaFrames);

};

const next = () => stepByFrames(directionStep.value);
const prev = () => stepByFrames(-directionStep.value);

const applyDragDeltaX = (deltaX: number) => {

  const pixels = pixelsPerFrameResolved.value;
  const deltaFramesFloat = (deltaX / pixels) * directionStep.value;
  fractionalFrameRemainder.value += deltaFramesFloat;

  const remainder = fractionalFrameRemainder.value;
  const step = remainder >= 0 ? Math.floor(remainder) : Math.ceil(remainder);

  if (step !== 0) {

    stepByFrames(step);
    fractionalFrameRemainder.value -= step;

  }

};

let dragRafId: number | null = null;
const pendingDragDeltaX = ref(0);

const flushPendingDrag = () => {

  dragRafId = null;

  if (!isInteracting.value) {

    pendingDragDeltaX.value = 0;

    return;

  }

  const dx = pendingDragDeltaX.value;
  pendingDragDeltaX.value = 0;

  if (!dx) {

    return;

  }

  applyDragDeltaX(dx);

  if (pendingDragDeltaX.value !== 0) {

    dragRafId = window.requestAnimationFrame(flushPendingDrag);

  }

};

const enqueueDragDeltaX = (dx: number) => {

  if (!dx) {

    return;

  }

  pendingDragDeltaX.value += dx;

  if (dragRafId !== null) {

    return;

  }

  dragRafId = window.requestAnimationFrame(flushPendingDrag);

};

const cancelDragRaf = () => {

  if (dragRafId === null) {

    return;

  }

  window.cancelAnimationFrame(dragRafId);
  dragRafId = null;
  pendingDragDeltaX.value = 0;

};

const preventScrollIfNeeded = (event: Event) => {

  if (!props.disableScrollEvent) {

    return;

  }

  if (!isInteracting.value) {

    return;

  }

  if (event.cancelable) {

    event.preventDefault();

  }

};

let removeGlobalScrollGuards: (() => void) | null = null;

const addGlobalScrollGuards = () => {

  if (!props.disableScrollEvent) {

    return;

  }

  if (removeGlobalScrollGuards) {

    return;

  }

  const onWheel = (e: WheelEvent) => preventScrollIfNeeded(e);
  const onTouchMove = (e: TouchEvent) => preventScrollIfNeeded(e);

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('touchmove', onTouchMove, { passive: false });

  removeGlobalScrollGuards = () => {

    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchmove', onTouchMove);
    removeGlobalScrollGuards = null;

  };

};

const endInteraction = () => {

  isInteracting.value = false;
  activePointerId.value = null;

  cancelDragRaf();

  if (removeGlobalScrollGuards) {

    removeGlobalScrollGuards();

  }

};

const onPointerDown = (event: PointerEvent) => {

  if (frameUrls.value.length <= 0) {

    return;

  }

  if (event.button !== 0 && event.pointerType !== 'touch') {

    return;

  }

  isInteracting.value = true;
  lastX.value = event.clientX;
  fractionalFrameRemainder.value = 0;
  pendingDragDeltaX.value = 0;
  cancelDragRaf();
  activePointerId.value = event.pointerId;

  containerEl.value?.focus?.();
  containerEl.value?.setPointerCapture?.(event.pointerId);

  addGlobalScrollGuards();

  stopAutoplay();

  preloadAroundCurrent();

};

const onPointerMove = (event: PointerEvent) => {

  if (!isInteracting.value) {

    return;

  }

  if (activePointerId.value !== null && event.pointerId !== activePointerId.value) {

    return;

  }

  let dx = 0;

  const anyEvent = event as unknown as { getCoalescedEvents?: () => PointerEvent[] };

  if (typeof anyEvent.getCoalescedEvents === 'function') {

    const events = anyEvent.getCoalescedEvents();
    let last = lastX.value;

    for (const e of events) {

      dx += e.clientX - last;
      last = e.clientX;

    }

    lastX.value = last;

  } else {

    dx = event.clientX - lastX.value;
    lastX.value = event.clientX;

  }

  enqueueDragDeltaX(dx);

};

const onPointerUp = (event: PointerEvent) => {

  if (!isInteracting.value) {

    return;

  }

  if (activePointerId.value !== null && event.pointerId !== activePointerId.value) {

    return;

  }

  endInteraction();

  if (isPlaying.value) {

    startAutoplay();

  }

};

const onKeydown = (event: KeyboardEvent) => {

  const total = frameUrls.value.length;

  if (total <= 0) {

    return;

  }

  if (event.key === 'ArrowLeft') {

    event.preventDefault();
    stepByFrames(-1);

  }

  if (event.key === 'ArrowRight') {

    event.preventDefault();
    stepByFrames(1);

  }

};

const onMouseEnter = () => {

  if (!props.pauseOnHover) {

    return;

  }
  isHovering.value = true;

};

const onMouseLeave = () => {

  if (!props.pauseOnHover) {

    return;

  }
  isHovering.value = false;

};

const containerStyle = computed<CSSProperties>(() => {

  if (!props.disableScrollEvent) {

    return {};

  }

  if (!isInteracting.value) {

    return {};

  }

  return {
    touchAction: 'none',
    userSelect: 'none',
  };

});

const imageContainerStyle = computed<CSSProperties>(() => ({
  transform: `scale(${zoom.value})`,
  transformOrigin: 'center',
}));

const preloadedUrls = new Set<string>();
let preloadGeneration = 0;
let lastPreloadSignature = '';

const buildPreloadOrder = (urls: string[], centerIndex: number): string[] => {

  const total = urls.length;

  if (total <= 0) {

    return [];

  }

  const center = Math.min(Math.max(centerIndex, 0), total - 1);
  const ordered: string[] = [];

  ordered.push(urls[center] as string);

  for (let offset = 1; ordered.length < total; offset += 1) {

    const right = center + offset;
    const left = center - offset;

    if (right < total) {

      ordered.push(urls[right] as string);

    }

    if (left >= 0) {

      ordered.push(urls[left] as string);

    }

  }

  return ordered;

};

const preloadUrl = (url: string) => {

  if (!url) {

    return;

  }

  if (preloadedUrls.has(url)) {

    return;

  }

  preloadedUrls.add(url);

  const img = new Image();
  img.decoding = 'async';
  img.src = url;

};

const preloadUrlsChunked = (urls: string[], batchSize = 10) => {

  if (typeof window === 'undefined') {

    return;

  }

  const generation = (preloadGeneration += 1);
  const w = window as unknown as { requestIdleCallback?: (cb: () => void) => number };
  let cursor = 0;

  const pump = () => {

    if (generation !== preloadGeneration) {

      return;

    }

    const end = Math.min(cursor + batchSize, urls.length);

    for (; cursor < end; cursor += 1) {

      preloadUrl(urls[cursor] as string);

    }

    if (cursor >= urls.length) {

      return;

    }

    if (typeof w.requestIdleCallback === 'function') {

      w.requestIdleCallback(pump);

      return;

    }

    setTimeout(pump, 16);

  };

  pump();

};

const preloadAroundCurrent = () => {

  const urls = frameUrls.value;

  if (!urls.length) {

    return;

  }

  const centerIndex = clampFrame(currentFrame.value) - 1;
  const radius = 18;
  const start = Math.max(centerIndex - radius, 0);
  const endExclusive = Math.min(centerIndex + radius + 1, urls.length);
  const slice = urls.slice(start, endExclusive);
  const ordered = buildPreloadOrder(slice, centerIndex - start);

  preloadUrlsChunked(ordered, 12);

};

const preloadAheadForAutoplay = () => {

  const urls = frameUrls.value;

  if (!urls.length) {

    return;

  }

  if (!isPlaying.value) {

    return;

  }

  const step = directionStep.value;
  const lookahead = 36;
  const ordered: string[] = [];
  let frame = clampFrame(currentFrame.value);

  for (let i = 0; i < lookahead; i += 1) {

    frame = wrapFrame(frame + step);
    ordered.push(urls[frame - 1] as string);

  }

  preloadUrlsChunked(ordered, 18);

};

const schedulePreload = () => {

  if (typeof window === 'undefined') {

    return;

  }

  const urls = frameUrls.value;

  if (!urls.length) {

    return;

  }

  const signature = `${urls.length}|${urls[0] ?? ''}|${urls[urls.length - 1] ?? ''}`;

  if (signature !== lastPreloadSignature) {

    lastPreloadSignature = signature;
    preloadedUrls.clear();

  }

  const centerIndex = clampFrame(currentFrame.value) - 1;
  const ordered = buildPreloadOrder(urls, centerIndex);

  preloadUrlsChunked(ordered, 10);

};

const normalizeLoopProp = (value: LoopProp): number | boolean => {

  if (value === true) {

    return true;

  }

  const n = Number(value);

  if (Number.isFinite(n) && n > 0) {

    return Math.floor(n);

  }

  return false;

};

const shouldAutoplayTick = computed(() => {

  if (!props.autoplay) {

    return false;

  }

  if (!isPlaying.value) {

    return false;

  }

  if (isInteracting.value) {

    return false;

  }

  if (!isInView.value) {

    return false;

  }

  if (props.pauseOnHover && isHovering.value) {

    return false;

  }

  return true;

});

const autoplayIntervalResolved = computed(() => {

  const ms = Number(props.autoplayIntervalMs);

  return Number.isFinite(ms) && ms > 0 ? ms : 100;

});

function stopAutoplay() {

  if (autoplayTimerId === null) {

    return;

  }

  window.clearInterval(autoplayTimerId);
  autoplayTimerId = null;

}

function stopPlaying() {

  isPlaying.value = false;
  stopAutoplay();

}

function autoplayStepOnce() {

  const total = frameUrls.value.length;

  if (total <= 0) {

    return;

  }

  const loop = normalizeLoopProp(props.loop);
  const step = directionStep.value;
  const endFrame = step > 0 ? total : 1;
  const wrapFrame = step > 0 ? 1 : total;
  const stopFrame = 1;

  if (!loop) {

    if (currentFrame.value === endFrame) {

      currentFrame.value = stopFrame;
      stopPlaying();

      return;

    }

    currentFrame.value = clampFrame(currentFrame.value + step);
    preloadAheadForAutoplay();

    return;

  }

  if (currentFrame.value === endFrame) {

    const maxRotations = typeof loop === 'number' ? loop : null;

    if (maxRotations !== null && rotationsCompleted.value >= maxRotations) {

      currentFrame.value = stopFrame;
      stopPlaying();

      return;

    }

    currentFrame.value = wrapFrame;
    preloadAheadForAutoplay();

    return;

  }

  const nextFrame = clampFrame(currentFrame.value + step);
  currentFrame.value = nextFrame;
  preloadAheadForAutoplay();

  if (nextFrame === endFrame) {

    rotationsCompleted.value += 1;

    const maxRotations = typeof loop === 'number' ? loop : null;

    if (maxRotations !== null && rotationsCompleted.value >= maxRotations) {

      currentFrame.value = stopFrame;
      stopPlaying();

    }

  }

}

function startAutoplay() {

  stopAutoplay();

  if (!isPlaying.value) {

    return;

  }

  preloadAheadForAutoplay();

  autoplayTimerId = window.setInterval(() => {

    if (!shouldAutoplayTick.value) {

      return;

    }

    autoplayStepOnce();

  }, autoplayIntervalResolved.value);

}

function togglePlay() {

  isPlaying.value = !isPlaying.value;

  if (isPlaying.value) {

    rotationsCompleted.value = 0;
    startAutoplay();

    return;

  }

  stopAutoplay();

}

function zoomIn() {

  zoom.value = Math.min(zoom.value + 0.25, 3);

}

function zoomOut() {

  zoom.value = Math.max(zoom.value - 0.25, 1);

}

function resetPosition() {

  zoom.value = 1;
  panMode.value = false;
  currentFrame.value = 1;
  fractionalFrameRemainder.value = 0;

}

let removeTouchFallback: (() => void) | null = null;

const addTouchFallback = () => {

  const el = containerEl.value;

  if (!el) {

    return;

  }

  if (removeTouchFallback) {

    return;

  }

  if ('PointerEvent' in window) {

    return;

  }

  const onTouchStart = (e: TouchEvent) => {

    const t = e.touches?.[0];

    if (!t) {

      return;

    }

    isInteracting.value = true;
    lastX.value = t.clientX;
    fractionalFrameRemainder.value = 0;
    addGlobalScrollGuards();
    stopAutoplay();

  };

  const onTouchMove = (e: TouchEvent) => {

    if (!isInteracting.value) {

      return;

    }

    const t = e.touches?.[0];

    if (!t) {

      return;

    }

    const dx = t.clientX - lastX.value;
    lastX.value = t.clientX;
    enqueueDragDeltaX(dx);
    preventScrollIfNeeded(e);

  };

  const onTouchEnd = () => {

    endInteraction();

    if (isPlaying.value) {

      startAutoplay();

    }

  };

  const onTouchCancel = () => {

    endInteraction();

    if (isPlaying.value) {

      startAutoplay();

    }

  };

  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: false });
  el.addEventListener('touchend', onTouchEnd);
  el.addEventListener('touchcancel', onTouchCancel);

  removeTouchFallback = () => {

    el.removeEventListener('touchstart', onTouchStart as EventListener);
    el.removeEventListener('touchmove', onTouchMove as EventListener);
    el.removeEventListener('touchend', onTouchEnd as EventListener);
    el.removeEventListener('touchcancel', onTouchCancel as EventListener);
    removeTouchFallback = null;

  };

};

onMounted(() => {

  schedulePreload();

  isPlaying.value = Boolean(props.autoplay);
  rotationsCompleted.value = 0;

  startAutoplay();

  if ('IntersectionObserver' in window) {

    isInView.value = false;

    intersectionObserver = new IntersectionObserver(
      (entries) => {

        const entry = entries[0];
        isInView.value = Boolean(entry?.isIntersecting);

      },
      { threshold: 0.15 },
    );

    if (containerEl.value) {

      intersectionObserver.observe(containerEl.value);

    }

  }

  addTouchFallback();

});

watch(containerEl, (el, prevEl) => {

  if (!intersectionObserver) {

    return;

  }

  if (prevEl) {

    intersectionObserver.unobserve(prevEl);

  }

  if (el) {

    intersectionObserver.observe(el);

  }

});

watch(frameUrls, () => {

  schedulePreload();
  currentFrame.value = clampFrame(currentFrame.value);

  rotationsCompleted.value = 0;
  startAutoplay();

});

watch(
  () => [props.autoplay, props.autoplayIntervalMs, props.loop, props.spinReverse],
  () => {

    isPlaying.value = Boolean(props.autoplay);
    rotationsCompleted.value = 0;
    startAutoplay();

  },
);

onBeforeUnmount(() => {

  endInteraction();

  stopAutoplay();

  if (intersectionObserver) {

    intersectionObserver.disconnect();
    intersectionObserver = null;

  }

  if (removeTouchFallback) {

    removeTouchFallback();

  }

});
</script>
